// Consolidated score calculation used by both ModelBuilder and Simulation.
// config: { baseScore, lowestRiskCategory, riskThresholds }
export const calculateScore = (type, riskType, weight, thresholds, config, value = null) => {
  const { baseScore, lowestRiskCategory, riskThresholds } = config;
  const isLow = lowestRiskCategory === 'LOW';

  if (type === 'Boolean') {
    // When value is explicitly false (Simulation checkbox unchecked), return 0
    if (value === false) return 0;

    switch (riskType) {
      case 'Low':
        return isLow ? weight * baseScore : 0;
      case 'Normal':
        return weight * baseScore * (isLow ? riskThresholds.lowToNormal : 1);
      case 'High':
        return weight * baseScore * riskThresholds.normalToHigh * (isLow ? riskThresholds.lowToNormal : 1);
      case 'VeryHigh':
        return weight * baseScore * riskThresholds.normalToHigh * riskThresholds.highToVeryHigh * (isLow ? riskThresholds.lowToNormal : 1);
      case 'Unacceptable':
        return weight * baseScore * riskThresholds.normalToHigh * riskThresholds.highToVeryHigh * riskThresholds.veryHighToUnacceptable * (isLow ? riskThresholds.lowToNormal : 1);
      default:
        return 0;
    }
  } else if (type === 'Numerical') {
    // No value provided: return threshold ranges (used in ModelBuilder display)
    if (value === null || value === undefined) {
      return {
        normal: isLow ? baseScore * riskThresholds.lowToNormal : 0,
        high: baseScore * riskThresholds.normalToHigh,
        veryHigh: baseScore * riskThresholds.normalToHigh * riskThresholds.highToVeryHigh,
        unacceptable: baseScore * riskThresholds.normalToHigh * riskThresholds.highToVeryHigh * riskThresholds.veryHighToUnacceptable
      };
    }

    if (value === 0 || value === '') return 0;

    const highThreshold = baseScore * riskThresholds.normalToHigh;
    const veryHighThreshold = highThreshold * riskThresholds.highToVeryHigh;
    const unacceptableThreshold = veryHighThreshold * riskThresholds.veryHighToUnacceptable;

    if (isLow && thresholds && thresholds.normal && value <= thresholds.normal) {
      return value * (baseScore * riskThresholds.lowToNormal / thresholds.normal);
    } else if (thresholds && value <= thresholds.high) {
      const startThreshold = isLow ? (thresholds.normal || 0) : 0;
      const normalScore = isLow ? baseScore * riskThresholds.lowToNormal : 0;
      const denominator = thresholds.high - startThreshold;
      if (denominator === 0) return highThreshold;
      return normalScore + (value - startThreshold) * ((highThreshold - normalScore) / denominator);
    } else if (thresholds && value <= thresholds.veryHigh) {
      const scorePerStep = (veryHighThreshold - highThreshold) / (thresholds.veryHigh - thresholds.high);
      return highThreshold + (value - thresholds.high) * scorePerStep;
    } else if (thresholds) {
      const denominator = (thresholds.unacceptable || thresholds.veryHigh) - thresholds.veryHigh;
      if (denominator === 0) return unacceptableThreshold;
      const scorePerStep = (unacceptableThreshold - veryHighThreshold) / denominator;
      return veryHighThreshold + (value - thresholds.veryHigh) * scorePerStep;
    }
  }
  return 0;
};

// Returns null if valid, or an error key string if invalid.
// Valid weight: a number > 0 and <= 100.
export const validateParameterWeight = (weight) => {
  const num = parseFloat(weight);
  if (isNaN(num) || num <= 0 || num > 100) {
    return 'weightInvalid';
  }
  return null;
};

// Predefined tags for parameter categorization (CUST-059)
export const PARAMETER_TAGS = ['KYC', 'Transaction', 'Jurisdiction', 'Behavior', 'PEP', 'Screening'];

export const sortParameters = (parameters) => {
  const riskTypeOrder = {
    'Normal': 1,
    'High': 2,
    'VeryHigh': 3,
    'Unacceptable': 4,
    'Numerical': 5
  };

  return [...parameters].sort((a, b) => {
    if (!a.riskType) return 1;
    if (!b.riskType) return -1;
    return riskTypeOrder[a.riskType] - riskTypeOrder[b.riskType];
  });
};
